import { useState, useEffect } from 'react';
import { Download, Loader2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../lib/exportUtils';
import { useNotifications } from '../store/useStore';
import type { Database } from '../types/database.types';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { resolveNoVatClauseText } from '../lib/noVatClause';
import {
  A4_PAGE_WIDTH,
  A4_PAGE_HEIGHT,
  A4_MARGIN,
  COMPANY_FOOTER_DEFAULT,
  COMPANY_INFO,
  MARCHIO_PUBLIC_PATH,
  LOGOTIPO_PUBLIC_PATH,
  loadImageScaledDataUrl,
  drawLetterhead,
  drawIssuerAndCustomerBoxes,
  drawPageFooter,
} from '../lib/letterheadPdf';
import { getDocumentTypeLabel } from '../lib/invoiceUtils';

type Invoice = Database['public']['Tables']['invoices']['Row'];
type InvoiceItem = Database['public']['Tables']['invoice_items']['Row'];
type Customer = Database['public']['Tables']['customers']['Row'];

type InvoiceInstallment = Database['public']['Tables']['invoice_installments']['Row'];

interface InvoiceWithDetails extends Invoice {
  customers?: Customer | null;
  invoice_items: (InvoiceItem & {
    products?: {
      id: string;
      code: string;
      name: string;
      unit?: string | null;
    } | null;
  })[];
  invoice_installments?: InvoiceInstallment[];
}

interface InvoicePrintViewProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
}

export function InvoicePrintView({ isOpen, onClose, invoiceId }: InvoicePrintViewProps) {
  const [invoice, setInvoice] = useState<InvoiceWithDetails | null>(null);
  // Nota di credito: la fattura che rettifica (va citata sul documento)
  const [referenceInvoice, setReferenceInvoice] = useState<{ invoice_number: string | null; invoice_date: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [companyFooter] = useState(COMPANY_FOOTER_DEFAULT);
  const { addNotification } = useNotifications();

  useEffect(() => {
    if (isOpen && invoiceId) {
      void loadInvoice();
    }
  }, [isOpen, invoiceId]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          customers (*),
          invoice_items (
            *,
            products (id, code, name, unit)
          ),
          invoice_installments (*)
        `)
        .eq('id', invoiceId)
        .single();

      if (error) throw error;
      const loaded = data as unknown as InvoiceWithDetails;

      let reference: { invoice_number: string | null; invoice_date: string } | null = null;
      if (loaded.document_type === 'credit_note' && loaded.reference_invoice_id) {
        const { data: ref } = await supabase
          .from('invoices')
          .select('invoice_number, invoice_date')
          .eq('id', loaded.reference_invoice_id)
          .maybeSingle();
        reference = ref ?? null;
      }
      setReferenceInvoice(reference);
      setInvoice(loaded);
    } catch (e) {
      console.error('Error loading invoice:', e);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Impossibile caricare la fattura',
      });
    } finally {
      setLoading(false);
    }
  };

  const isCreditNote = invoice?.document_type === 'credit_note';

  const creditNoteLines = (): string[] => {
    if (!invoice || !isCreditNote) return [];
    const lines: string[] = [];
    if (referenceInvoice) {
      const refDate = new Date(referenceInvoice.invoice_date).toLocaleDateString('it-IT');
      lines.push(`Rettifica della fattura n. ${referenceInvoice.invoice_number || '—'} del ${refDate}`);
    }
    if (invoice.credit_note_reason) lines.push(`Motivo: ${invoice.credit_note_reason}`);
    return lines;
  };

  const generatePdfBlob = async (): Promise<Blob> => {
    if (!invoice) throw new Error('Dati fattura non disponibili');

    const noVatForfettario = (invoice as any).no_vat_forfettario !== false;
    const [marchioDataUrl, logotipoDataUrl] = await Promise.all([
      loadImageScaledDataUrl(MARCHIO_PUBLIC_PATH, 200),
      loadImageScaledDataUrl(LOGOTIPO_PUBLIC_PATH, 440),
    ]);

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = A4_PAGE_WIDTH;
    const pageHeight = A4_PAGE_HEIGHT;
    const margin = A4_MARGIN;

    drawLetterhead(doc, marchioDataUrl, logotipoDataUrl, pageWidth, margin);

    let yPos = drawIssuerAndCustomerBoxes(doc, invoice.customers || undefined, 48, pageWidth, margin);

    // Riga oggetto: titolo doc + numero + data + scadenza
    const docLabel = getDocumentTypeLabel((invoice as any).document_type || 'invoice');
    const docNumber = invoice.invoice_number ? ` n. ${invoice.invoice_number}` : '';
    const docDate = invoice.invoice_date
      ? new Date(invoice.invoice_date).toLocaleDateString('it-IT')
      : '';

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(55, 65, 81);
    doc.text(`${docLabel}${docNumber}`, margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    doc.text(`Data: ${docDate}`, pageWidth - margin, yPos, { align: 'right' });

    if (invoice.payment_due_date) {
      yPos += 5;
      doc.setFontSize(9);
      const due = new Date(invoice.payment_due_date).toLocaleDateString('it-IT');
      doc.text(`Scadenza pagamento: ${due}`, pageWidth - margin, yPos, { align: 'right' });
    }

    // Nota di credito: fattura rettificata e motivo (art. 26 DPR 633/72)
    const creditLines = creditNoteLines();
    if (creditLines.length > 0) {
      yPos += 5;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(55, 65, 81);
      const wrapped = doc.splitTextToSize(creditLines.join('\n'), pageWidth - 2 * margin);
      doc.text(wrapped, margin, yPos);
      yPos += (wrapped.length - 1) * 4;
    }

    yPos += 6;

    // Tabella righe
    const showVatColumn = !noVatForfettario;
    const head: any[] = [
      ['Codice', 'Descrizione', 'Q.tà', 'Prezzo', 'Sconto']
        .concat(showVatColumn ? ['IVA%'] : [])
        .concat(['Imponibile']),
    ];

    let totalTaxable = 0;
    const tableRows: any[] = invoice.invoice_items.map((item) => {
      const code = item.products?.code || '';
      const desc = item.description || item.products?.name || '';
      const qty = `${item.quantity} ${item.unit || item.products?.unit || ''}`.trim();
      const price = formatCurrency(item.unit_price);
      const discount = `${item.discount_percentage || 0}%`;
      const rowSubtotal = item.quantity * item.unit_price;
      const rowDiscount = rowSubtotal * ((item.discount_percentage || 0) / 100);
      const rowTaxable = rowSubtotal - rowDiscount;
      totalTaxable += rowTaxable;
      const row: any[] = [code, desc, qty, price, discount];
      if (showVatColumn) row.push(`${item.vat_rate}%`);
      row.push(formatCurrency(rowTaxable));
      return row;
    });

    const taxAmount = noVatForfettario ? 0 : (invoice.tax_amount || 0);
    const bollo = (invoice as any).marca_da_bollo_amount || 0;
    const total = invoice.total_amount || totalTaxable + taxAmount + bollo;

    const footRows: any[] = [];
    footRows.push([
      {
        content: 'IMPONIBILE',
        colSpan: showVatColumn ? 6 : 5,
        styles: { halign: 'right', fontStyle: 'bold', fillColor: [243, 244, 246], textColor: [107, 114, 128] },
      },
      {
        content: formatCurrency(totalTaxable),
        styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 255], textColor: [55, 65, 81] },
      },
    ]);

    if (!noVatForfettario) {
      footRows.push([
        {
          content: 'IVA',
          colSpan: 6,
          styles: { halign: 'right', fillColor: [243, 244, 246], textColor: [107, 114, 128] },
        },
        {
          content: formatCurrency(taxAmount),
          styles: { halign: 'right', fillColor: [255, 255, 255], textColor: [55, 65, 81] },
        },
      ]);
    }

    if (bollo > 0) {
      footRows.push([
        {
          content: 'Marca da bollo',
          colSpan: showVatColumn ? 6 : 5,
          styles: { halign: 'right', fillColor: [243, 244, 246], textColor: [107, 114, 128] },
        },
        {
          content: formatCurrency(bollo),
          styles: { halign: 'right', fillColor: [255, 255, 255], textColor: [55, 65, 81] },
        },
      ]);
    }

    footRows.push([
      {
        content: 'TOTALE',
        colSpan: showVatColumn ? 6 : 5,
        styles: {
          halign: 'right',
          fontStyle: 'bold',
          fontSize: 10,
          fillColor: [243, 244, 246],
          textColor: [17, 24, 39],
        },
      },
      {
        content: formatCurrency(total),
        styles: {
          halign: 'right',
          fontStyle: 'bold',
          fontSize: 10,
          fillColor: [255, 255, 255],
          textColor: [15, 23, 42],
        },
      },
    ]);

    autoTable(doc, {
      startY: yPos,
      head,
      body: tableRows,
      foot: footRows,
      margin: { left: margin, right: margin, bottom: 40 },
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2, lineColor: [229, 231, 235], lineWidth: 0.1 },
      headStyles: { fillColor: [0, 162, 154], textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: showVatColumn
        ? {
            0: { cellWidth: 22 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 16, halign: 'center' },
            3: { cellWidth: 22, halign: 'right' },
            4: { cellWidth: 16, halign: 'center' },
            5: { cellWidth: 14, halign: 'center' },
            6: { cellWidth: 26, halign: 'right', fontStyle: 'bold', textColor: [15, 23, 42] },
          }
        : {
            0: { cellWidth: 22 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 16, halign: 'center' },
            3: { cellWidth: 22, halign: 'right' },
            4: { cellWidth: 16, halign: 'center' },
            5: { cellWidth: 26, halign: 'right', fontStyle: 'bold', textColor: [15, 23, 42] },
          },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      didDrawPage: () => {
        drawPageFooter(doc, companyFooter, pageWidth, pageHeight, margin);
      },
    });

    let currentY = (doc as any).lastAutoTable.finalY + 8;

    // Clausola esenzione forfettario
    if (noVatForfettario) {
      const clause = resolveNoVatClauseText((invoice as any).no_vat_clause_text);
      if (currentY > pageHeight - 55) {
        doc.addPage();
        currentY = 20;
        drawPageFooter(doc, companyFooter, pageWidth, pageHeight, margin);
      }
      // Clausola su una sola riga, evidenziata in giallo (effetto evidenziatore, niente riquadro).
      const usableW = pageWidth - 2 * margin;
      doc.setFont('helvetica', 'italic');
      let clauseFs = 8;
      doc.setFontSize(clauseFs);
      while (doc.getTextWidth(clause) > usableW && clauseFs > 6) {
        clauseFs -= 0.5;
        doc.setFontSize(clauseFs);
      }
      const clauseW = Math.min(doc.getTextWidth(clause), usableW);
      const emMm = clauseFs * 0.3528;
      const ascent = emMm * 0.72;
      const descent = emMm * 0.25;
      const padV = 0.8;
      const baselineY = currentY + ascent + padV / 2;
      doc.setFillColor(253, 224, 71); // giallo evidenziatore
      doc.rect(margin - 0.5, currentY, clauseW + 1, ascent + descent + padV, 'F');
      doc.setTextColor(31, 41, 55);
      doc.text(clause, margin, baselineY);
      currentY += ascent + descent + padV + 4;
    }

    // Piano rate (se presente)
    const installments = (invoice.invoice_installments || []).slice().sort((a, b) => a.installment_number - b.installment_number);
    if (installments.length > 0) {
      if (currentY > pageHeight - 60) {
        doc.addPage();
        currentY = 20;
        drawPageFooter(doc, companyFooter, pageWidth, pageHeight, margin);
      }
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 64, 175);
      doc.text('PIANO PAGAMENTI', margin, currentY);
      currentY += 2;
      autoTable(doc, {
        startY: currentY + 1,
        head: [['#', 'Descrizione', 'Scadenza', '%', 'Importo']],
        body: installments.map((r) => [
          String(r.installment_number),
          r.label || '',
          new Date(r.due_date).toLocaleDateString('it-IT'),
          `${r.percentage}%`,
          formatCurrency(Number(r.amount)),
        ]),
        margin: { left: margin, right: margin, bottom: 40 },
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 1.8, lineColor: [229, 231, 235], lineWidth: 0.1 },
        headStyles: { fillColor: [30, 64, 175], textColor: [255, 255, 255], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 28, halign: 'center' },
          3: { cellWidth: 16, halign: 'right' },
          4: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
        },
        didDrawPage: () => {
          drawPageFooter(doc, companyFooter, pageWidth, pageHeight, margin);
        },
      });
      currentY = (doc as any).lastAutoTable.finalY + 6;
    }

    // Nota marca da bollo virtuale
    if (bollo > 0) {
      if (currentY > pageHeight - 45) {
        doc.addPage();
        currentY = 20;
        drawPageFooter(doc, companyFooter, pageWidth, pageHeight, margin);
      }
      const bolloNote = `Imposta di bollo di € ${bollo.toFixed(2)} assolta in modo virtuale ai sensi del DM 17/06/2014.`;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(80, 80, 80);
      const bLines = doc.splitTextToSize(bolloNote, pageWidth - 2 * margin);
      doc.text(bLines, margin, currentY);
      currentY += bLines.length * 3.8 + 4;
    }

    // Dati bancari per bonifico + Causale + Modalità pagamento.
    // Non sulla nota di credito: lì il cliente non deve pagare nulla.
    const iban = ((invoice as any).iban as string | null) || COMPANY_INFO.iban;
    const causale = (invoice as any).causale as string | null;
    const paymentTerms = invoice.payment_terms || '';

    if (!isCreditNote && (iban || causale || paymentTerms)) {
      if (currentY > pageHeight - 55) {
        doc.addPage();
        currentY = 20;
        drawPageFooter(doc, companyFooter, pageWidth, pageHeight, margin);
      }
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 64, 175);
      doc.text('DATI BANCARI PER BONIFICO', margin, currentY);
      currentY += 5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(55, 65, 81);
      if (iban) {
        doc.setFont('helvetica', 'bold');
        doc.text(`Intestatario:`, margin, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(COMPANY_INFO.ragioneSociale, margin + 24, currentY);
        currentY += 4;
        doc.setFont('helvetica', 'bold');
        doc.text(`IBAN:`, margin, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(iban, margin + 24, currentY);
        currentY += 4;
        doc.setFont('helvetica', 'bold');
        doc.text(`Banca:`, margin, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(COMPANY_INFO.banca, margin + 24, currentY);
        currentY += 4;
      }
      if (causale) {
        doc.setFont('helvetica', 'bold');
        doc.text(`Causale:`, margin, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(causale, margin + 24, currentY);
        currentY += 4;
      }
      currentY += 4;
    }

    // Note
    if (invoice.notes) {
      if (currentY > pageHeight - 40) {
        doc.addPage();
        currentY = 20;
        drawPageFooter(doc, companyFooter, pageWidth, pageHeight, margin);
      }
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 64, 175);
      doc.text('NOTE', margin, currentY);
      currentY += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(55, 65, 81);
      const noteLines = doc.splitTextToSize(invoice.notes, pageWidth - 2 * margin);
      doc.text(noteLines, margin, currentY);
    }

    const buf = doc.output('arraybuffer');
    return new Blob([buf], { type: 'application/pdf' });
  };

  const handleDownloadPDF = async () => {
    if (!invoice || isGeneratingPDF) return;
    setIsGeneratingPDF(true);
    try {
      const blob = await generatePdfBlob();
      const url = URL.createObjectURL(blob);
      const today = new Date().toLocaleDateString('it-IT').replace(/\//g, '-');
      const customerName = invoice.customers?.company_name || 'Cliente';
      const safeName = customerName.replace(/[^a-zA-Z0-9]/g, '_');
      const number = invoice.invoice_number || invoice.id.slice(0, 8);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fattura_${number}_${safeName}_${today}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      requestAnimationFrame(() => {
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 2000);
      });
      addNotification({ type: 'success', title: 'PDF generato', message: 'Fattura scaricata' });
    } catch (e) {
      console.error(e);
      const msg = e instanceof Error ? e.message : 'Errore generazione PDF';
      addNotification({ type: 'error', title: 'Errore PDF', message: msg });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (!isOpen) return null;

  const noVatForfettario = (invoice as any)?.no_vat_forfettario !== false;
  const docLabel = invoice ? getDocumentTypeLabel((invoice as any).document_type || 'invoice') : '';

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-[calc(100vw-0.5rem)] sm:w-[95vw] max-w-3xl h-[calc(100dvh-0.5rem)] sm:h-auto max-h-[calc(100dvh-0.5rem)] sm:max-h-[95vh] bg-white rounded-lg shadow-xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2 border-b">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm sm:text-base font-semibold truncate">
              Anteprima {docLabel}
              {invoice?.invoice_number ? ` n. ${invoice.invoice_number}` : ''}
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              type="button"
              size="sm"
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF || loading || !invoice}
              className="h-7 text-xs gap-1 bg-teal-600 hover:bg-teal-700 text-white"
            >
              {isGeneratingPDF ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Genera PDF
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 sm:h-7 sm:w-7">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-2 sm:p-6 bg-gray-50">
          {loading || !invoice ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Caricamento...
            </div>
          ) : (
            <div className="bg-white rounded shadow-sm p-4 sm:p-6 space-y-4 text-sm min-w-[640px]">
              <div className="text-center border-b pb-3">
                <div className="text-2xl font-bold text-teal-600">ABRUZZOAI</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-teal-50 border border-teal-100 rounded p-3 text-xs">
                  <div className="font-bold text-teal-700 text-[10px] tracking-wide mb-1">DATI EMITTENTE</div>
                  <div className="font-semibold text-gray-800">{COMPANY_INFO.ragioneSociale}</div>
                  <div className="text-gray-600 space-y-0.5 mt-0.5">
                    <div>{COMPANY_INFO.indirizzo}</div>
                    <div>{COMPANY_INFO.cap} {COMPANY_INFO.citta} ({COMPANY_INFO.provincia})</div>
                    <div>P.IVA: {COMPANY_INFO.partitaIva}</div>
                    <div>C.F.: {COMPANY_INFO.codiceFiscale}</div>
                    <div>REA: {COMPANY_INFO.rea}</div>
                    <div>Regime: {COMPANY_INFO.regimeFiscaleCode} - {COMPANY_INFO.regimeFiscale}</div>
                    <div>PEC: {COMPANY_INFO.pec}</div>
                    <div>{COMPANY_INFO.email} • Tel: {COMPANY_INFO.telefono}</div>
                  </div>
                </div>

                <div className="bg-teal-50 border border-teal-100 rounded p-3 text-xs">
                  <div className="font-bold text-teal-700 text-[10px] tracking-wide mb-1">SPETT.LE CLIENTE</div>
                  <div className="font-semibold text-gray-800">{invoice.customers?.company_name || 'Cliente'}</div>
                  <div className="text-gray-600 space-y-0.5 mt-0.5">
                    {invoice.customers?.address && <div>{invoice.customers.address}</div>}
                    {(invoice.customers?.postal_code || invoice.customers?.city || invoice.customers?.province) && (
                      <div>
                        {[invoice.customers?.postal_code, invoice.customers?.city, invoice.customers?.province && `(${invoice.customers.province})`]
                          .filter(Boolean)
                          .join(' ')}
                      </div>
                    )}
                    {invoice.customers?.vat_number && <div>P.IVA: {invoice.customers.vat_number}</div>}
                    {invoice.customers?.tax_code && <div>C.F.: {invoice.customers.tax_code}</div>}
                    {invoice.customers?.email && <div>Email: {invoice.customers.email}</div>}
                    {invoice.customers?.phone && <div>Tel: {invoice.customers.phone}</div>}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-end text-sm border-y py-2">
                <div className="font-bold text-base text-gray-800">
                  {docLabel}
                  {invoice.invoice_number ? ` n. ${invoice.invoice_number}` : ''}
                </div>
                <div className="text-right text-xs text-gray-600 space-y-0.5">
                  <div>
                    Data emissione:{' '}
                    <span className="font-semibold text-gray-800">
                      {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('it-IT') : '—'}
                    </span>
                  </div>
                  {invoice.payment_due_date && (
                    <div>
                      Scadenza:{' '}
                      <span className="font-semibold text-gray-800">
                        {new Date(invoice.payment_due_date).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {creditNoteLines().length > 0 && (
                <div className="text-xs text-gray-700 space-y-0.5">
                  {creditNoteLines().map((line) => <div key={line}>{line}</div>)}
                </div>
              )}

              <table className="w-full text-xs border">
                <thead className="bg-teal-600 text-white">
                  <tr>
                    <th className="p-1 text-left">Codice</th>
                    <th className="p-1 text-left">Descrizione</th>
                    <th className="p-1 text-center">Q.tà</th>
                    <th className="p-1 text-right">Prezzo</th>
                    <th className="p-1 text-center">Sconto</th>
                    {!noVatForfettario && <th className="p-1 text-center">IVA%</th>}
                    <th className="p-1 text-right">Imponibile</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.invoice_items.map((it) => {
                    const sub = it.quantity * it.unit_price;
                    const dis = sub * ((it.discount_percentage || 0) / 100);
                    const tax = sub - dis;
                    return (
                      <tr key={it.id} className="border-t">
                        <td className="p-1">{it.products?.code || ''}</td>
                        <td className="p-1">{it.description}</td>
                        <td className="p-1 text-center">{it.quantity} {it.unit || ''}</td>
                        <td className="p-1 text-right">{formatCurrency(it.unit_price)}</td>
                        <td className="p-1 text-center">{it.discount_percentage || 0}%</td>
                        {!noVatForfettario && <td className="p-1 text-center">{it.vat_rate}%</td>}
                        <td className="p-1 text-right font-semibold">{formatCurrency(tax)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="flex justify-end gap-4 text-sm flex-wrap">
                <div>
                  <span className="text-gray-600">Imponibile: </span>
                  <span className="font-semibold">{formatCurrency(invoice.taxable_amount || 0)}</span>
                </div>
                {noVatForfettario ? (
                  <div className="text-amber-700 font-medium">Esente IVA</div>
                ) : (
                  <div>
                    <span className="text-gray-600">IVA: </span>
                    <span className="font-semibold">{formatCurrency(invoice.tax_amount || 0)}</span>
                  </div>
                )}
                {((invoice as any).marca_da_bollo_amount || 0) > 0 && (
                  <div>
                    <span className="text-gray-600">Bollo: </span>
                    <span className="font-semibold">{formatCurrency((invoice as any).marca_da_bollo_amount)}</span>
                  </div>
                )}
                <div className="font-bold text-base">
                  TOTALE: {formatCurrency(invoice.total_amount || 0)}
                </div>
              </div>

              {noVatForfettario && (
                <div className="text-xs italic">
                  <span className="bg-yellow-200 text-gray-900 box-decoration-clone px-1 py-0.5">
                    {resolveNoVatClauseText((invoice as any).no_vat_clause_text)}
                  </span>
                </div>
              )}

              {((invoice as any).marca_da_bollo_amount || 0) > 0 && (
                <div className="text-xs italic text-gray-600">
                  Imposta di bollo di € {((invoice as any).marca_da_bollo_amount || 0).toFixed(2)} assolta in modo virtuale ai sensi del DM 17/06/2014.
                </div>
              )}

              {(invoice.invoice_installments && invoice.invoice_installments.length > 0) && (
                <div className="text-xs border-t pt-2">
                  <div className="font-semibold text-blue-800 mb-1">PIANO PAGAMENTI</div>
                  <table className="w-full border text-[11px]">
                    <thead className="bg-blue-50 text-blue-900">
                      <tr>
                        <th className="p-1 text-center">#</th>
                        <th className="p-1 text-left">Descrizione</th>
                        <th className="p-1 text-center">Scadenza</th>
                        <th className="p-1 text-right">%</th>
                        <th className="p-1 text-right">Importo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.invoice_installments
                        .slice()
                        .sort((a, b) => a.installment_number - b.installment_number)
                        .map((r) => (
                          <tr key={r.id} className="border-t">
                            <td className="p-1 text-center">{r.installment_number}</td>
                            <td className="p-1">{r.label || ''}</td>
                            <td className="p-1 text-center">{new Date(r.due_date).toLocaleDateString('it-IT')}</td>
                            <td className="p-1 text-right">{r.percentage}%</td>
                            <td className="p-1 text-right font-semibold">{formatCurrency(Number(r.amount))}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(() => {
                const ibanShown = (invoice as any).iban || COMPANY_INFO.iban;
                const causale = (invoice as any).causale as string | null;
                const pt = invoice.payment_terms;
                return !isCreditNote && (ibanShown || causale || pt) && (
                  <div className="text-xs border-t pt-2 space-y-0.5">
                    <div className="font-semibold text-blue-800">DATI BANCARI PER BONIFICO</div>
                    {ibanShown && <div>Intestatario: <span className="font-semibold">{COMPANY_INFO.ragioneSociale}</span></div>}
                    {ibanShown && <div className="font-mono">IBAN: {ibanShown}</div>}
                    {ibanShown && <div>Banca: <span className="font-semibold">{COMPANY_INFO.banca}</span></div>}
                    {causale && <div>Causale: {causale}</div>}
                  </div>
                );
              })()}

              {invoice.notes && (
                <div className="text-xs border-t pt-2">
                  <div className="font-semibold text-blue-800 mb-1">NOTE</div>
                  <div className="whitespace-pre-line">{invoice.notes}</div>
                </div>
              )}

              <div className="border-t pt-2 mt-4 text-[10px] text-gray-500 text-center leading-relaxed">
                <div>
                  {COMPANY_INFO.ragioneSociale} • {COMPANY_INFO.indirizzo}, {COMPANY_INFO.cap} {COMPANY_INFO.citta} ({COMPANY_INFO.provincia})
                </div>
                <div>
                  P.IVA {COMPANY_INFO.partitaIva} • C.F. {COMPANY_INFO.codiceFiscale} • REA {COMPANY_INFO.rea} • {COMPANY_INFO.regimeFiscaleCode} {COMPANY_INFO.regimeFiscale}
                </div>
                <div>
                  {COMPANY_INFO.sito} • {COMPANY_INFO.email} • PEC {COMPANY_INFO.pec} • Tel {COMPANY_INFO.telefono}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
